"use client";
import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { track } from "@vercel/analytics";

import { cn } from "@/lib/utils";
import { usePathname } from "next/navigation";

const buttonVariants = cva(
  "scale-95 md:scale-100 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[15px] font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive cursor-pointer font-medium",
  {
    variants: {
      variant: {
        default:
          "bg-primary-300 text-foreground-900 shadow-xs hover:bg-primary-400",
        destructive:
          "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20",
        outline:
          "border border-primary-300 text-primary-300 bg-transparent shadow-xs hover:bg-primary-300 hover:text-foreground-900",
        secondary:
          "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        link: "text-foreground-100 underline-offset-4 hover:text-primary-300 !h-auto !p-0 font-medium",
        "link-primary":
          "underline-offset-4 hover:underline !h-auto !p-0 text-primary-300 font-medium hover:text-primary-400",
      },
      size: {
        default: "h-12 px-6 py-3 has-[>svg]:px-3",
        xs: "h-10 px-4 py-2 has-[>svg]:px-2 text-xs",
        sm: "h-12 gap-1.5 px-6 has-[>svg]:px-3",
        lg: "h-14 text-base px-8 has-[>svg]:px-4",
        icon: "size-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  trackEvent,
  trackProps,
  ...props
}) {
  const Comp = asChild ? Slot : "button";

  const pathname = usePathname();
  const handleClick = (event) => {
    if (props.onClick) {
      props.onClick(event);
    }

    if (trackEvent) {
      track(trackEvent, { url: pathname, ...trackProps });
    }
  };

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      onClick={handleClick}
      {...props}
    />
  );
}

export { Button, buttonVariants };
